/**
 * Unified Kiosk Preview Component
 * Reusable kiosk preview that can be used anywhere in the app
 * Store-independent - all state passed via props
 */

import React from 'react';
import { KioskUICustomization, ConnectorInfo, PendingMediaUpload } from '../../types/kiosk-types';
import { KioskHeader } from './KioskHeader';
import { KioskConnectorCard } from './KioskConnectorCard';
import { KioskFooter } from './KioskFooter';
import { IdleScreenOverlay } from './IdleScreenOverlay';

interface KioskPreviewProps {
  /** Kiosk configuration */
  config: KioskUICustomization;

  /** List of connectors to display */
  connectors?: ConnectorInfo[];

  /** Optional pending logo URL for live preview */
  pendingLogoUrl?: string;

  /** Optional idle screen media */
  pendingIdleScreen?: PendingMediaUpload;

  /** Whether to show idle screen overlay */
  showIdleScreen?: boolean;

  /** Optional click handler for connectors */
  onConnectorClick?: (connector: ConnectorInfo) => void;
}

// Default connector data if none provided
const DEFAULT_4INCH_CONNECTORS: ConnectorInfo[] = [
  { id: '01', type: 'ccs', power: 50, status: 'available' },
  { id: '02', type: 'chademo', power: 50, status: 'connected' },
  { id: '03', type: 'type2', power: 22, status: 'occupied' },
];

const DEFAULT_5INCH_CONNECTORS: ConnectorInfo[] = [
  { id: '01', type: 'ccs', power: 50, status: 'available' },
  { id: '02', type: 'chademo', power: 50, status: 'connected' },
  { id: '03', type: 'type2', power: 22, status: 'occupied' },
  { id: '04', type: 'ccs', power: 50, status: 'unavailable' },
  { id: '05', type: 'type2', power: 22, status: 'available' },
];

/**
 * Unified Kiosk Preview Component
 * Single implementation used everywhere in the app
 */
export function KioskPreview({
  config,
  connectors,
  pendingLogoUrl,
  pendingIdleScreen,
  showIdleScreen = false,
  onConnectorClick,
}: KioskPreviewProps) {
  const isSmall = config.formFactor === '4inch';
  const size = isSmall ? 'small' : 'large';

  // Use provided connectors or defaults
  const displayConnectors = connectors || (isSmall ? DEFAULT_4INCH_CONNECTORS : DEFAULT_5INCH_CONNECTORS);

  // Get logo URL
  const mediaLogo = isSmall ? config.icons.media4Logo : config.icons.media5Logo;
  const logoSrc = pendingLogoUrl || mediaLogo?.s3Url || mediaLogo?.fallbackUrl || config.icons.logo?.s3Url || config.icons.logo?.fallbackUrl;

  if (isSmall) {
    // 4-inch layout
    return (
      <div
        className="relative flex flex-col overflow-hidden rounded-lg shadow-2xl mx-auto"
        style={{
          width: '480px',
          maxWidth: '100%',
          height: '480px',
          backgroundColor: config.theme.colors.primaryBackground,
          border: '2px solid #333',
        }}
      >
        {/* Header with logo and title */}
        <div style={{ position: 'relative', zIndex: 60 }}>
          <KioskHeader
            config={config}
            size="small"
            showTitle={true}
            pendingLogoUrl={logoSrc}
            useMedia4Logo={true}
          />
        </div>

        {/* Main content area - connector cards */}
        <div
          className="kiosk-connector-list flex flex-1 flex-col overflow-y-auto"
          style={{
            backgroundColor: config.theme.colors.primaryBackground,
            position: 'relative',
          }}
        >
          <div className="space-y-2 px-4 py-2">
            {displayConnectors.map((connector) => (
              <div
                key={connector.id}
                onClick={() => onConnectorClick?.(connector)}
                style={{ cursor: onConnectorClick ? 'pointer' : 'default' }}
              >
                <KioskConnectorCard
                  connector={connector}
                  config={config}
                  size="small"
                />
              </div>
            ))}
          </div>

          {/* Top Fade Gradient */}
          <div
            className="absolute top-0 left-0 right-0"
            style={{
              height: '55px',
              background: 'linear-gradient(180deg, rgba(10, 10, 10, 0.8) 0%, rgba(10, 10, 10, 0) 100%)',
              pointerEvents: 'none',
              zIndex: 20,
            }}
            aria-hidden="true"
          />

          {/* Left Bottom Fade Gradient */}
          <div
            className="absolute bottom-0 left-0"
            style={{
              width: '100px',
              height: '60px',
              background: 'linear-gradient(90deg, rgba(10, 10, 10, 0.9) 0%, rgba(10, 10, 10, 0) 100%)',
              pointerEvents: 'none',
              zIndex: 20,
            }}
            aria-hidden="true"
          />

          {/* Right Bottom Fade Gradient */}
          <div
            className="absolute bottom-0 right-0"
            style={{
              width: '100px',
              height: '60px',
              background: 'linear-gradient(270deg, rgba(10, 10, 10, 0.9) 0%, rgba(10, 10, 10, 0) 100%)',
              pointerEvents: 'none',
              zIndex: 20,
            }}
            aria-hidden="true"
          />
        </div>

        {/* Footer with payment icons */}
        <KioskFooter config={config} size="small" />

        {/* Idle Screen Overlay */}
        <IdleScreenOverlay
          screenSize="small"
          config={config}
          pendingIdleScreen={pendingIdleScreen}
          showInPreview={showIdleScreen}
        />
      </div>
    );
  }

  // 5-inch layout
  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-lg shadow-2xl mx-auto"
      style={{
        width: '420px',
        maxWidth: '100%',
        height: '720px',
        backgroundColor: config.theme.colors.primaryBackground,
        border: '2px solid #333',
      }}
    >
      {/* Header with centered logo and icons */}
      <div
        className="relative flex shrink-0 flex-col items-center"
        style={{
          backgroundColor: config.theme.colors.primaryBackground,
          paddingTop: '17px',
          paddingBottom: '12px',
          zIndex: 60,
        }}
      >
        {/* Top row: Language icon, Logo (centered), Menu icon */}
        <div className="relative flex w-full items-center justify-between px-9" style={{ marginBottom: '23px' }}>
          {/* Language icon - left */}
          <div
            className="flex items-center justify-center rounded"
            style={{
              width: '57px',
              height: '59px',
              backgroundColor: '#212121',
              border: '1px solid #414040',
            }}
          >
            {config.icons.languageIcon?.s3Url || config.icons.languageIcon?.fallbackUrl ? (
              <img
                src={config.icons.languageIcon.s3Url || config.icons.languageIcon.fallbackUrl}
                alt="Language"
                style={{ width: '41px', height: '41px', objectFit: 'contain' }}
              />
            ) : (
              <img
                src="/figma-assets/kiosk/language-icon.svg"
                alt="Language"
                style={{ width: '41px', height: '41px' }}
              />
            )}
          </div>

          {/* Logo - centered */}
          <div
            className="absolute left-1/2 flex -translate-x-1/2 items-center justify-center"
            style={{
              width: '123px',
              height: '52px',
            }}
          >
            {logoSrc ? (
              <img
                key={logoSrc}
                src={logoSrc}
                alt="Logo"
                className="max-h-full max-w-full"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  e.currentTarget.src = '/figma-assets/kiosk/nayax-energy-logo.svg';
                }}
              />
            ) : (
              <img
                src="/figma-assets/kiosk/nayax-energy-logo.svg"
                alt="Nayax Energy"
                className="h-full w-full object-contain"
              />
            )}
          </div>

          {/* Menu icon - right */}
          <div
            className="flex items-center justify-center rounded"
            style={{
              width: '55.5px',
              height: '59px',
              backgroundColor: '#212121',
              border: '1px solid #414040',
            }}
          >
            {config.icons.menuIcon?.s3Url || config.icons.menuIcon?.fallbackUrl ? (
              <img
                src={config.icons.menuIcon.s3Url || config.icons.menuIcon.fallbackUrl}
                alt="Menu"
                style={{ width: '24.5px', height: '24.5px', objectFit: 'contain' }}
              />
            ) : (
              <img
                src="/figma-assets/kiosk/menu-icon.svg"
                alt="Menu"
                style={{ width: '24.5px', height: '24.5px' }}
              />
            )}
          </div>
        </div>

        {/* Title - centered below header */}
        <div className="flex items-center justify-center">
          <h2
            className="text-center font-normal"
            style={{
              fontSize: '27px',
              color: config.theme.colors.primaryText,
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 400,
              lineHeight: '0.857',
            }}
          >
            Select Connector
          </h2>
        </div>

        {/* Divider line */}
        <div
          className="mt-4"
          style={{
            width: '169px',
            height: '1px',
            background: 'linear-gradient(180deg, rgba(42, 42, 42, 0) 0%, rgba(65, 64, 64, 1) 52%, rgba(42, 42, 42, 0) 100%)',
          }}
        />
      </div>

      {/* Main content area */}
      <div
        className="kiosk-connector-list flex flex-1 flex-col overflow-y-auto"
        style={{
          backgroundColor: config.theme.colors.primaryBackground,
          position: 'relative',
        }}
      >
        {/* Connector cards */}
        <div className="space-y-2 px-5 pb-5">
          {displayConnectors.map((connector) => (
            <div
              key={connector.id}
              onClick={() => onConnectorClick?.(connector)}
              style={{ cursor: onConnectorClick ? 'pointer' : 'default' }}
            >
              <KioskConnectorCard
                connector={connector}
                config={config}
                size="large"
              />
            </div>
          ))}
        </div>

        {/* Top Fade Gradient */}
        <div
          className="absolute top-0 left-0 right-0"
          style={{
            height: '55px',
            background: 'linear-gradient(180deg, rgba(10, 10, 10, 0.8) 0%, rgba(10, 10, 10, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 20,
          }}
          aria-hidden="true"
        />

        {/* Left Bottom Fade Gradient */}
        <div
          className="absolute bottom-0 left-0"
          style={{
            width: '100px',
            height: '60px',
            background: 'linear-gradient(90deg, rgba(10, 10, 10, 0.9) 0%, rgba(10, 10, 10, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 20,
          }}
          aria-hidden="true"
        />

        {/* Right Bottom Fade Gradient */}
        <div
          className="absolute bottom-0 right-0"
          style={{
            width: '100px',
            height: '60px',
            background: 'linear-gradient(270deg, rgba(10, 10, 10, 0.9) 0%, rgba(10, 10, 10, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 20,
          }}
          aria-hidden="true"
        />
      </div>

      {/* Footer with payment icons */}
      <KioskFooter config={config} size="large" />

      {/* Idle Screen Overlay */}
      <IdleScreenOverlay
        screenSize="medium"
        config={config}
        pendingIdleScreen={pendingIdleScreen}
        showInPreview={showIdleScreen}
      />
    </div>
  );
}
